import { z } from "zod";
import {
  FREIGHT_MODES,
  MAX_BULK_ORDERS,
  MAX_ITEM_DISCOUNT_PCT4,
  MAX_PRICE_CENTS,
  MAX_SALES_ORDER_ITEMS,
  MAX_STOCK_QTY_Q4,
  isIsoDate,
  type BaseState,
  type EffectState,
  type EffectStep,
  type FreightMode,
  type StatusTone,
} from "@cx/domain";
import { listQuery } from "./common";
import { blank, text } from "./fields";
import { ulidParam } from "./ids";
import { businessDate, type InstallmentView } from "./prices";
import type { StatusView } from "./statuses";
import type { FiscalDocumentStatus } from "./fiscal-documents";

// ===== Pedidos de venda (RF-VEN-02) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const cents = (what: string) =>
  z
    .number(`Informe ${what}`)
    .int("Use centavos inteiros")
    .min(0, "Valor não pode ser negativo")
    .max(MAX_PRICE_CENTS, "Valor acima do limite");
const pct = z
  .number("Informe o percentual")
  .int("Percentual inválido")
  .min(0, "O desconto vai de 0% a 100%")
  .max(MAX_ITEM_DISCOUNT_PCT4, "O desconto vai de 0% a 100%");
const optDate = blank.pipe(businessDate.nullable());

/**
 * Item: produto cadastrado (descrição, código e unidade vêm do cadastro) ou outro item avulso (`productId` nulo,
 * descrição e preço obrigatórios). `listPriceCents` nulo no produto = preço da lista do pedido. `warehouseId` nulo =
 * depósito do pedido (ou o padrão da empresa).
 */
export const salesOrderItemInput = z
  .object({
    productId: nullish(ulidParam),
    description: text(1000),
    unit: text(6),
    qtyQ4: z
      .number("Informe a quantidade")
      .int("Quantidade inválida")
      .min(1, "A quantidade deve ser maior que zero")
      .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite"),
    listPriceCents: nullish(cents("o preço")),
    discountPct4: pct.default(0),
    warehouseId: nullish(ulidParam),
  })
  .superRefine((v, ctx) => {
    if (v.productId !== null) return;
    if (!v.description)
      ctx.addIssue({ code: "custom", path: ["description"], message: "Informe a descrição do item" });
    if (v.listPriceCents === null)
      ctx.addIssue({ code: "custom", path: ["listPriceCents"], message: "Informe o preço do item" });
  });
export type SalesOrderItemInput = z.output<typeof salesOrderItemInput>;
export type SalesOrderItemFormValues = z.input<typeof salesOrderItemInput>;

export const salesOrderInput = z
  .object({
    date: businessDate,
    expectedDate: optDate,
    contactId: ulidParam,
    customerPo: text(60),
    sellerId: nullish(ulidParam),
    priceListId: nullish(ulidParam),
    warehouseId: nullish(ulidParam),
    items: z
      .array(salesOrderItemInput, "Informe os itens")
      .min(1, "Inclua ao menos um item")
      .max(MAX_SALES_ORDER_ITEMS, `No máximo ${MAX_SALES_ORDER_ITEMS} itens`),
    /** Desconto geral em % sobre os itens; null = em valor (`discountCents`). */
    discountPct4: nullish(pct),
    discountCents: z
      .number("Informe o desconto")
      .int("Use centavos inteiros")
      .min(0, "Valor não pode ser negativo")
      .default(0),
    freightCents: cents("o frete").default(0),
    otherExpensesCents: cents("as outras despesas").default(0),
    /** Condição de pagamento: gera as parcelas (05 §4) a partir da data do pedido. */
    paymentTermId: nullish(ulidParam),
    carrierId: nullish(ulidParam),
    freightMode: z.literal(FREIGHT_MODES, "Modalidade do frete inválida").default(9),
    notes: text(5000),
    internalNotes: text(5000),
  })
  .superRefine((v, ctx) => {
    // Zod 4 roda o refinamento mesmo com a data inválida: só compara datas válidas.
    if (v.expectedDate && isIsoDate(v.expectedDate) && isIsoDate(v.date) && v.expectedDate < v.date)
      ctx.addIssue({
        code: "custom",
        path: ["expectedDate"],
        message: "A data prevista deve ser igual ou posterior à data do pedido",
      });
  });
export type SalesOrderInput = z.output<typeof salesOrderInput>;
export type SalesOrderFormValues = z.input<typeof salesOrderInput>;

export const salesOrderListQuery = listQuery.extend({
  statusId: ulidParam.optional(),
  sellerId: ulidParam.optional(),
  from: businessDate.optional(),
  to: businessDate.optional(),
});
export type SalesOrderListQuery = z.output<typeof salesOrderListQuery>;

/** Mudança de situação: a situação que a tela mostrava é obrigatória (409 `status_changed`). */
export const salesOrderStatusInput = z.object({ toStatusId: ulidParam, expectedFromStatusId: ulidParam });
export type SalesOrderStatusInput = z.output<typeof salesOrderStatusInput>;

const unique =
  <T>(key: (v: T) => string) =>
  (list: T[]) =>
    new Set(list.map(key)).size === list.length;
const bulkList = <T extends z.ZodType>(item: T, key: (v: z.output<T>) => string) =>
  z
    .array(item, "Selecione os pedidos")
    .min(1, "Selecione ao menos um pedido")
    .max(MAX_BULK_ORDERS, `No máximo ${MAX_BULK_ORDERS} pedidos por vez`)
    .refine(unique(key), "Pedido repetido na seleção");

/** Lote "mudar situação": cada pedido com a situação que a lista mostrava. */
export const salesOrderBulkStatusInput = z.object({
  toStatusId: ulidParam,
  items: bulkList(z.object({ id: ulidParam, expectedFromStatusId: ulidParam }), (v) => v.id),
});
export type SalesOrderBulkStatusInput = z.output<typeof salesOrderBulkStatusInput>;

/** Lote "imprimir": um PDF com os pedidos na ordem da seleção. */
export const salesOrderBulkPrintInput = z.object({ ids: bulkList(ulidParam, (v) => v) });
export type SalesOrderBulkPrintInput = z.output<typeof salesOrderBulkPrintInput>;

export type SalesOrderStatusRef = { id: string; label: string; color: StatusTone; baseState: BaseState };

/** Item calculado. Sem custo nem % da lista; `unitCostCents` só para quem tem products:read_cost. */
export type SalesOrderItemView = {
  position: number;
  productId: string | null;
  description: string;
  code: string | null;
  unit: string | null;
  qtyQ4: number;
  listPriceCents: number;
  discountPct4: number;
  unitPriceCents: number;
  totalCents: number;
  warehouseId: string | null;
  /**
   * Comissão do vendedor no item (05 §6); null = pedido sem vendedor. Ausente sem sellers:read (o usuário-vendedor vê
   * a dele) e sempre no PDF.
   */
  commissionPct4?: number | null;
  commissionCents?: number | null;
  unitCostCents?: number | null;
};

export type SalesOrderCalc = {
  items: SalesOrderItemView[];
  itemsTotalCents: number;
  itemsDiscountCents: number;
  discountPct4: number | null;
  discountCents: number;
  freightCents: number;
  otherExpensesCents: number;
  totalCents: number;
  itemsCount: number;
  quantitySumQ4: number;
  /** Ausente sem sellers:read (o usuário-vendedor vê a dele) e no PDF, como a comissão do item. */
  commissionTotalCents?: number;
  installments: InstallmentView[];
};

type Ref = { id: string; name: string } | null;

export type SalesOrderView = SalesOrderCalc & {
  id: string;
  number: number;
  date: string;
  expectedDate: string | null;
  contact: { id: string; name: string; document: string | null };
  customerPo: string | null;
  seller: Ref;
  priceList: Ref;
  paymentTerm: Ref;
  warehouse: Ref;
  carrier: Ref;
  freightMode: FreightMode;
  notes: string | null;
  internalNotes: string | null;
  origin: "manual" | "quote" | "channel" | "pos";
  quote: { id: string; number: number } | null;
  status: SalesOrderStatusRef;
  nextStatuses: SalesOrderStatusRef[];
  /** Estado do estoque (nenhum, reservado, lançado) e das contas. */
  effects: EffectState;
  /** Motivo da trava de edição/exclusão; null = pode editar. */
  lockedReason: string | null;
  /** Última NF-e do pedido (T2.3); null = nenhuma. */
  invoice: { id: string; status: FiscalDocumentStatus; number: number | null } | null;
  /** Faturamento agrupado ativo do pedido (T2.7); null = nenhum. */
  groupedBilling: { id: string; number: number } | null;
  createdAt: string;
  updatedAt: string;
};

export type SalesOrderListItem = {
  id: string;
  number: number;
  date: string;
  contactName: string;
  sellerName: string | null;
  totalCents: number;
  status: SalesOrderStatusRef;
};

/** `totalCents` só na primeira página (null nas seguintes ou acima de 2^53). */
export type SalesOrderListResult = {
  data: SalesOrderListItem[];
  nextCursor: string | null;
  summary: { count: number; totalCents: number | null };
};

export type SalesOrderFormOptions = {
  priceLists: { id: string; name: string; active: boolean }[];
  paymentTerms: { id: string; name: string; active: boolean }[];
  sellers: { id: string; name: string }[];
  /** Usuário-vendedor: o vendedor do pedido é sempre ele (o servidor força). */
  sellerLocked: boolean;
  carriers: { id: string; name: string }[];
  warehouses: { id: string; name: string; isDefault: boolean }[];
  statuses: StatusView[];
  canAddOtherItems: boolean;
};

export type SalesOrderStatusHistoryItem = {
  id: string;
  from: { id: string; label: string } | null;
  to: { id: string; label: string };
  /** Sem transição = ação manual (lançar/estornar) ou conversão. */
  manual: boolean;
  steps: EffectStep[];
  userId: string | null;
  userName: string | null;
  createdAt: string;
};

export type SalesOrderActionResult = { steps: EffectStep[] };
export type SalesOrderCreated = { id: string; number: number };

export type SalesOrderBulkItemResult = {
  id: string;
  ok: boolean;
  changed?: boolean;
  code?: string;
  message?: string;
};
export type SalesOrderBulkResult = {
  results: SalesOrderBulkItemResult[];
  okCount: number;
  failCount: number;
};

export type SalesOrderPdfLink = { url: string; fileName: string };
export type SalesOrderBulkPrintResult = SalesOrderPdfLink & { printed: string[]; missing: string[] };

/** Dados do PDF (montados no DO, desenhados no Worker). Endereços já em uma linha. */
export type SalesOrderPrintData = {
  order: SalesOrderView;
  company: {
    name: string;
    tradeName: string | null;
    document: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
  };
  contact: {
    name: string;
    document: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
  };
};
